import React, { useContext, useState } from 'react';
import { firebaseApp } from '@/infrastructure/firebase/firebase-app';
import {
  FIRESTORE_COLLECTIONS,
  FIRESTORE_DOCS,
} from '@/infrastructure/enum/firestore';
import type { User } from 'firebase/auth';

const getFirestoreClient = async () => {
  const firestore = await import('firebase/firestore');
  return {
    db: firestore.getFirestore(firebaseApp),
    ...firestore,
  };
};

const getUserDocRef = (db: any, docFn: any, userId: string) =>
  docFn(db, FIRESTORE_COLLECTIONS.USERS, userId);

interface IUserState {
  name: null | string;
  email: null | string;
  id: null | string;
  photoURL?: null | string;
}

export interface IUserUpdate {
  userId: string;
  name?: string;
}

export interface IUserContext {
  user: IUserState;
  loadUserInfo: (user: User) => void;
  changeUserName: ({ userId, name }: IUserUpdate) => void;
  deleteAccount: ({ userId }: IUserUpdate) => void;
}

export const UserContext = React.createContext<IUserContext | null>(null);

interface IUserProvider {
  children?: React.ReactNode;
}

const initialState = {
  id: null,
  name: null,
  email: null,
  photoURL: null,
} as IUserState;

const UserProvider: React.FC<IUserProvider> = ({ children }) => {
  const [user, setUser] = useState(initialState);

  const setUserInfo = (userInfo: IUserState) => {
    setUser({
      id: userInfo.id,
      name: userInfo.name,
      email: userInfo.email,
      photoURL: userInfo.photoURL,
    });
  };

  const setUserName = (name: string) => {
    setUser({
      ...user,
      name,
    });
  };

  const loadUserInfo = async (user: User) => {
    const { db, doc, getDoc, setDoc } = await getFirestoreClient();
    const docRef = getUserDocRef(db, doc, user.uid);
    const document = await getDoc(docRef);
    let userInfo = document.data() as IUserState;
    if (!userInfo) {
      userInfo = {
        name: `${user.displayName}`,
        email: `${user.email}`,
        id: `${user.uid}`,
      };

      await setDoc(docRef, userInfo);
      const favoriteDocRef = doc(
        db,
        FIRESTORE_COLLECTIONS.USERS,
        user.uid,
        FIRESTORE_COLLECTIONS.FAVORITES,
        FIRESTORE_DOCS.RECITERS
      );
      await setDoc(favoriteDocRef, {
        ids: [],
      });
    }

    setUserInfo({
      ...userInfo,
      id: user.uid,
      photoURL: user.photoURL,
    });
  };

  const changeUserName = async ({ userId, name }: IUserUpdate) => {
    if (name) {
      const { db, doc, updateDoc } = await getFirestoreClient();
      const docRef = getUserDocRef(db, doc, userId);
      await updateDoc(docRef, {
        name,
      });
      setUserName(name);
    }
  };

  const deleteAccount = async ({ userId }: IUserUpdate) => {
    const { db, collection, deleteDoc, doc, getDocs, query, where } =
      await getFirestoreClient();
    const queue: Promise<any>[] = [];
    const userDocRef = getUserDocRef(db, doc, userId);
    const collectionRef = collection(
      db,
      FIRESTORE_COLLECTIONS.SHARED_PLAYLISTS
    );
    const q = query(collectionRef, where('ownerID', '==', userId));
    const playlistDocs = await getDocs(q);
    playlistDocs.forEach((doc) => {
      queue.push(deleteDoc(doc.ref));
    });
    await Promise.all(queue);
    await deleteDoc(userDocRef);
  };

  const value = {
    user,
    loadUserInfo,
    changeUserName,
    deleteAccount,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUserContext = () => useContext(UserContext) as IUserContext;

export default UserProvider;
